<script setup lang="ts">
import type { WorkspaceOrder } from "@artech-global/types";

defineOptions({ name: "ManualSaleModal" });

const emit = defineEmits<{ (e: "saved"): void }>();

const { t } = useI18n();
const { $api } = useNuxtApp();
const toast = useToast();
const { setErrors, clearErrors, formErrors, errorMsg } = useResponseError();

const isOpen = ref(false);
const saveLoading = ref(false);
const editingID = ref("");
const presetWorkspaceID = ref("");

const workspaceOptions = ref<{ value: string; label: string }[]>([]);
const planOptions = ref<{ value: string; label: string }[]>([]);

const defaultForm = () => ({
  workspaceID: "",
  planID: "",
  type: "new",
  amount: 0,
  currency: "VND",
  monthCount: 1,
  billingCycle: "monthly",
  description: "",
});

const form = reactive(defaultForm());

// A custom contract is priced per deal, so it carries no plan and no billing
// cycle — those fields would only invite a number that means nothing.
const isCustom = computed(() => form.type === "custom");
// Editing corrects what was charged; which workspace and which plan it was
// booked against are what the record is, so they stay put.
const isEditing = computed(() => Boolean(editingID.value));

const typeOptions = computed(() => [
  { value: "new", label: t("finance.sale.type_new") },
  { value: "renew", label: t("finance.sale.type_renew") },
  { value: "change", label: t("finance.sale.type_change") },
  { value: "custom", label: t("finance.sale.type_custom") },
]);

const currencyOptions = [
  { value: "VND", label: "VND" },
  { value: "USD", label: "USD" },
];

const billingCycleOptions = [
  { value: "monthly", label: "monthly" },
  { value: "quarterly", label: "quarterly" },
  { value: "semi-annual", label: "semi-annual" },
  { value: "annual", label: "annual" },
];

async function loadOptions() {
  const [workspaces, plans] = await Promise.all([
    $api<{ items: { id: string; name: string }[] }>("/workspaces", { query: { pageSize: 100 } }),
    $api<{ items: { id: string; name: string; key: string }[] }>("/workspace-plans", { query: { pageSize: 100 } }),
  ]);
  workspaceOptions.value = (workspaces.items ?? []).map((w) => ({ value: w.id, label: w.name }));
  planOptions.value = (plans.items ?? []).map((p) => ({ value: p.id, label: `${p.name} (${p.key})` }));
}

async function open(order?: WorkspaceOrder, preset?: { workspaceID: string; type?: string }) {
  clearErrors();
  Object.assign(form, defaultForm());
  editingID.value = order ? String(order.id) : "";
  presetWorkspaceID.value = preset?.workspaceID ?? "";
  try {
    await loadOptions();
  } catch (err: any) {
    // Without this the click just does nothing: open() rejects and the caller
    // never awaited it.
    toast.error({ title: err?.data?.message || t("message.loadFailed") });
    return;
  }
  if (order) {
    Object.assign(form, {
      workspaceID: String(order.workspaceID ?? ""),
      planID: String(order.planID ?? ""),
      type: order.type ?? "new",
      amount: order.amount ?? 0,
      currency: order.currency ?? "VND",
      monthCount: order.monthCount ?? 1,
      billingCycle: order.billingCycle ?? "monthly",
      description: order.description ?? "",
    });
  } else if (preset) {
    form.workspaceID = preset.workspaceID;
    if (preset.type) form.type = preset.type;
  }
  isOpen.value = true;
}

async function submit() {
  saveLoading.value = true;
  clearErrors();
  try {
    if (isEditing.value) {
      await $api("/workspace-orders", {
        method: "PUT",
        body: {
          id: editingID.value,
          amount: form.amount,
          currency: form.currency,
          description: form.description,
          ...(isCustom.value ? {} : { monthCount: form.monthCount }),
        },
      });
    } else {
      const { monthCount, billingCycle, planID, ...custom } = form;
      await $api("/workspace-orders", {
        method: "POST",
        body: isCustom.value ? custom : { ...form },
      });
    }
    toast.success({ title: t("finance.sale.success") });
    isOpen.value = false;
    emit("saved");
  } catch (err) {
    setErrors(err);
  } finally {
    saveLoading.value = false;
  }
}

defineExpose({ open });
</script>

<template>
  <Modal
    v-model:open="isOpen"
    :title="isEditing ? t('finance.sale.editTitle') : t('finance.sale.title')"
    :description="isCustom ? t('finance.sale.customHelp') : t('finance.sale.help')"
  >
    <div class="flex flex-col gap-medium">
      <InlineMessage v-if="errorMsg" variant="negative">{{ errorMsg }}</InlineMessage>
      <InlineMessage v-if="isEditing" variant="info">{{ t("finance.sale.editHelp") }}</InlineMessage>

      <FormSelect
        v-model="form.workspaceID"
        :label="t('finance.sale.workspace')"
        :options="workspaceOptions"
        :disabled="isEditing || Boolean(presetWorkspaceID)"
        :error="formErrors.workspaceID"
      />
      <FormSelect
        v-model="form.type"
        :label="t('finance.sale.type')"
        :options="typeOptions"
        :disabled="isEditing"
        :error="formErrors.type"
      />
      <FormSelect
        v-if="!isCustom"
        v-model="form.planID"
        :label="t('finance.sale.plan')"
        :options="planOptions"
        :disabled="isEditing"
        :error="formErrors.planID"
      />

      <div class="flex flex-col gap-xsmall md:flex-row md:gap-small">
        <FormNumber
          v-model="form.amount"
          :label="t('finance.cost.amount')"
          :error="formErrors.amount"
          class="w-full md:w-1/2"
        />
        <FormSelect
          v-model="form.currency"
          :label="t('field.currency')"
          :options="currencyOptions"
          :error="formErrors.currency"
          class="w-full md:w-1/2"
        />
      </div>

      <!-- A custom contract is money received, not a term being billed, so it
           carries no month count and no cycle. -->
      <div v-if="!isCustom" class="flex flex-col gap-xsmall md:flex-row md:gap-small">
        <FormNumber
          v-model="form.monthCount"
          :label="t('finance.sale.monthCount')"
          :error="formErrors.monthCount"
          class="w-full md:w-1/2"
        />
        <FormSelect
          v-model="form.billingCycle"
          :label="t('finance.sale.billingCycle')"
          :options="billingCycleOptions"
          :disabled="isEditing"
          :error="formErrors.billingCycle"
          class="w-full md:w-1/2"
        />
      </div>

      <FormField
        v-model="form.description"
        :label="isCustom ? t('finance.sale.contractName') : t('field.description')"
        :error="formErrors.description"
      />
    </div>

    <template #actions>
      <Button variant="tertiary" size="small" @click="isOpen = false">
        {{ t("action.cancel") }}
      </Button>
      <Button variant="primary" size="small" :disabled="saveLoading" @click="submit">
        {{ t("action.save") }}
      </Button>
    </template>
  </Modal>
</template>
